/**
 * WithdrawModal Component
 * Modal for withdrawing USDT to TON wallet
 */

import { useState } from 'react';
import { api } from '../services/api';

interface WithdrawModalProps {
  isOpen: boolean;
  onClose: () => void;
  usdtBalance: number;
  onWithdrawSuccess: () => void;
  hasMiningPackage?: boolean;
  hasPendingWithdrawal?: boolean;
}

export function WithdrawModal({ isOpen, onClose, usdtBalance, onWithdrawSuccess, hasMiningPackage = true, hasPendingWithdrawal = false }: WithdrawModalProps) {
  const [amount, setAmount] = useState('');
  const [wallet, setWallet] = useState('');
  const [memo, setMemo] = useState('');
  const FEE_PERCENT = 18;
  const MIN_USDT = 50;
  const MAX_USDT = 500;
  const FLAT_TOKEN_FEE = 55_000;
  const [isLoading, setIsLoading] = useState(false);

  const calculateOutput = () => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return { gross: 0, fee: 0, net: 0 };

    const fee = numAmount * (FEE_PERCENT / 100);
    const net = numAmount - fee;

    return { gross: numAmount, fee, net };
  };

  const handleWithdraw = async () => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (numAmount < MIN_USDT) {
      alert(`Minimum withdrawal amount is ${MIN_USDT} USDT`);
      return;
    }

    if (numAmount > MAX_USDT) {
      alert(`Maximum withdrawal amount is $${MAX_USDT} USDT per transaction`);
      return;
    }

    if (numAmount > usdtBalance) {
      alert('Insufficient USDT balance');
      return;
    }

    if (!wallet || wallet.trim().length === 0) {
      alert('Please enter your TON wallet address');
      return;
    }

    setIsLoading(true);
    try {
      await api.requestWithdrawal(numAmount, wallet.trim(), memo.trim() || undefined);

      alert('Withdrawal request submitted!\n\nВаша заявка на вывод будет подтверждена в течение 48 часов. В исключительных случаях обработка может занять до 2 недель.');
      onWithdrawSuccess();
      onClose();
      setAmount('');
      setWallet('');
      setMemo('');
    } catch (error: any) {
      console.error('Withdrawal failed:', error);
      alert(error.response?.data?.message || 'Withdrawal request failed');
    } finally {
      setIsLoading(false);
    }
  };

  const output = calculateOutput();
  const isBlocked = !hasMiningPackage || hasPendingWithdrawal;

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
    }}>
      <div style={{
        background: 'linear-gradient(180deg, #1a2332 0%, #0a0e1a 100%)',
        borderRadius: '20px',
        padding: '24px',
        maxWidth: '400px',
        width: '100%',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        maxHeight: '90vh',
        overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 700 }}>Withdraw USDT</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: '24px',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* Blocked state: no mining package */}
        {!hasMiningPackage && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            padding: '20px',
            textAlign: 'center',
            marginBottom: '20px',
          }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>⛏️</div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#ef4444', marginBottom: '8px' }}>
              У вас нет активного Mining-пакета.
            </div>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
              Для вывода средств необходимо иметь активный Mining-пакет.
            </div>
          </div>
        )}

        {/* Blocked state: pending withdrawal */}
        {hasMiningPackage && hasPendingWithdrawal && (
          <div style={{
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '12px',
            padding: '20px',
            textAlign: 'center',
            marginBottom: '20px',
          }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#f59e0b', marginBottom: '8px' }}>
              У вас уже есть активная заявка на вывод.
            </div>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
              Дождитесь её подтверждения.
            </div>
          </div>
        )}

        {!isBlocked && (
          <>
            {/* Amount Input */}
            <div style={{
              background: 'rgba(10, 22, 40, 0.6)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
            }}>
              <div style={{
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.6)',
                marginBottom: '8px',
              }}>
                Amount (min {MIN_USDT} — max {MAX_USDT} USDT)
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  style={{
                    flex: 1,
                    background: 'none',
                    border: 'none',
                    color: '#fff',
                    fontSize: '24px',
                    fontWeight: 600,
                    outline: 'none',
                  }}
                />
                <div style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#2e7d32',
                }}>
                  USDT
                </div>
              </div>
              <div style={{
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.6)',
                marginTop: '8px',
              }}>
                Balance: {usdtBalance.toFixed(2)} USDT
              </div>
            </div>

            {/* Wallet Input */}
            <div style={{
              background: 'rgba(10, 22, 40, 0.6)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
            }}>
              <div style={{
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.6)',
                marginBottom: '8px',
              }}>
                Wallet Address
              </div>
              <input
                type="text"
                value={wallet}
                onChange={(e) => setWallet(e.target.value)}
                placeholder="UQD..."
                style={{
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            </div>

            {/* Memo Input */}
            <div style={{
              background: 'rgba(10, 22, 40, 0.6)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
            }}>
              <div style={{
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.6)',
                marginBottom: '8px',
              }}>
                Memo (не обязательно)
              </div>
              <input
                type="text"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder=""
                style={{
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            </div>

            {/* Fee Info */}
            <div style={{
              background: 'rgba(10, 22, 40, 0.6)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '8px',
                fontSize: '14px',
              }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Amount:</span>
                <span style={{ color: '#fff', fontWeight: 600 }}>{output.gross.toFixed(2)} USDT</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '8px',
                fontSize: '14px',
              }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Funding Fee ({FEE_PERCENT}%):</span>
                <span style={{ color: '#ef4444', fontWeight: 600 }}>-{output.fee.toFixed(2)} USDT</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '8px',
                fontSize: '14px',
              }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Burning Fee:</span>
                <span style={{ color: '#ef4444', fontWeight: 600 }}>-{FLAT_TOKEN_FEE.toLocaleString()} WiTon</span>
              </div>
              <div style={{
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                paddingTop: '8px',
                marginTop: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '16px',
              }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.8)' }}>You will receive:</span>
                <span style={{ color: '#10b981', fontWeight: 700 }}>{output.net.toFixed(2)} USDT</span>
              </div>
            </div>

            {/* Warning */}
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              padding: '12px',
              marginBottom: '20px',
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.8)',
              lineHeight: 1.5,
            }}>
              ⚠️ Ваша заявка на вывод будет подтверждена в течение 48 часов. В исключительных случаях обработка может занять до 2 недель.
            </div>

            {/* Withdraw Button */}
            <button
              onClick={handleWithdraw}
              disabled={isLoading || !amount || !wallet || parseFloat(amount) < MIN_USDT || parseFloat(amount) > usdtBalance}
              style={{
                width: '100%',
                padding: '16px',
                background: isLoading || !amount || !wallet || parseFloat(amount) < MIN_USDT || parseFloat(amount) > usdtBalance
                  ? 'rgba(59, 130, 246, 0.3)'
                  : 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                border: 'none',
                borderRadius: '12px',
                color: '#fff',
                fontSize: '18px',
                fontWeight: 700,
                cursor: isLoading || !amount || !wallet || parseFloat(amount) < MIN_USDT || parseFloat(amount) > usdtBalance ? 'not-allowed' : 'pointer',
                boxShadow: isLoading || !amount || !wallet || parseFloat(amount) < MIN_USDT || parseFloat(amount) > usdtBalance
                  ? 'none'
                  : '0 4px 20px rgba(16, 185, 129, 0.4)',
              }}
            >
              {isLoading ? 'Processing...' : 'Request Withdrawal'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
